<template>
  <div
    ref="chartContainer"
    class="chart-container"
    role="group"
    tabindex="0"
    :aria-label="accessibilityLabel"
    :aria-describedby="summaryId"
  >
    <p :id="summaryId" class="chart-visually-hidden">{{ accessibilitySummary }}</p>
    <apexchart
      ref="apexChart"
      :height="`${height}px`"
      type="line"
      :options="chartOptions"
      :series="series"
      @mounted="onChartMounted"
      @updated="onChartUpdated"
    ></apexchart>
  </div>
</template>

<style>
.apexcharts-tooltip {
  background-color: var(--kate-background-body) !important;
  color: var(--kate-type-light);
}

.apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {
  background: var(--kate-background);
}

.apexcharts-xaxistooltip {
  background-color: var(--kate-background-body) !important;
}

.apexcharts-toolbar {
  display: flex;
  gap: 15px;
  font-size: 1.5em;
}

.apexcharts-toolbar > div {
  color: var(--kate-secondary);
}

.apexcharts-toolbar > div:hover {
  color: var(--kate-secondary-dark);
}

.apexcharts-toolbar > div:focus-visible,
.apexcharts-legend-series:focus-visible {
  outline: 2px solid var(--kate-secondary);
  outline-offset: 2px;
}

.apexcharts-menu {
  display: none;
}

.chart-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>

<script>
import { uuid } from '@utils/uuid-wrapper';

export default {
  data() {
    return {
      summaryId: `chart-summary-${uuid()}`,
    };
  },

  props: {
    yaxisMax: {
      type: Number,
      default: undefined,
    },
    xaxisData: {
      type: Array,
      default: () => [],
    },
    xaxisTitle: {
      type: String,
      default: '',
    },
    yaxisTitle: {
      type: String,
      default: '',
    },
    series: {
      type: Array,
      default: () => [],
    },
    colours: {
      type: Array,
      default: () => [],
    },
    dashArray: {
      type: Array,
      default: () => [],
    },
    strokeCurve: {
      type: [String, Array],
      default: 'smooth',
    },
    stepSize: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 300,
    },
    customTooltip: {
      type: Function,
      default: undefined,
    },
    yaxisFormatter: {
      type: Function,
      default: undefined,
    },
    accessibilityLabel: {
      type: String,
      default: 'Line chart',
    },
    accessibilityDescription: {
      type: String,
      default: '',
    },
    xaxisAnnotations: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    accessibilitySummary() {
      const seriesNames = this.series.map(entry => entry.name).filter(Boolean);
      const baseSummary = `Chart contains ${seriesNames.length} series across ${this.xaxisData.length} points.`;
      const seriesSummary = seriesNames.length ? ` Series: ${seriesNames.join(', ')}.` : '';
      const customDescription = this.accessibilityDescription ? ` ${this.accessibilityDescription}` : '';
      return `${baseSummary}${seriesSummary}${customDescription}`.trim();
    },
    chartOptions() {
      return {
        chart: {
          type: 'line',
          width: '100%',
          sparkline: {
            enabled: false,
          },
          toolbar: {
            show: true,
            tools: {
              zoom: '<i class="far fa-object-group" aria-hidden="true"></i>',
              zoomin: '<i class="fas fa-search-plus" aria-hidden="true"></i>',
              zoomout: '<i class="fas fa-search-minus" aria-hidden="true"></i>',
              pan: false,
              download: false,
              reset: false,
              selection: false,
            },
          },
        },
        noData: {
          text: 'No data available',
          align: 'center',
          verticalAlign: 'middle',
          style: {
            fontSize: '20px',
            color: 'rgba(255, 255, 255, .5)',
          },
        },
        legend: {
          show: true,
          showForSingleSeries: true,
          labels: {
            colors: 'var(--kate-type-primary)',
            useSeriesColors: false,
          },
          itemMargin: {
            horizontal: 5,
            vertical: 15,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
          markers: {
            size: 3,
            radius: 2,
          },
        },
        grid: {
          show: true,
          borderColor: 'var(--kate-background-alpha)',
          padding: {
            right: this.xaxisAnnotations.length ? 16 : 0,
          },
        },
        responsive: [{
          breakpoint: 1000,
          options: {},
        }],
        stroke: {
          width: 3,
          curve: this.strokeCurve,
          dashArray: this.dashArray.length ? this.dashArray : undefined,
        },
        colors: this.colours.length ? this.colours : undefined,
        markers: {
          size: 4,
          strokeColors: 'var(--kate-panel-alpha)',
          hover: {
            sizeOffset: 3,
          },
        },
        xaxis: {
          type: 'category',
          categories: this.xaxisData,
          tickAmount: this.xaxisData.length || undefined,
          title: {
            text: this.xaxisTitle,
            style: {
              color: 'var(--kate-type-alt-dark)',
              fontSize: '12px',
            },
          },
          axisBorder: {
            show: true,
            color: 'var(--kate-background-alpha)',
            height: 1,
            width: '100%',
            offsetX: 0,
            offsetY: 0,
          },
          labels: {
            show: true,
            maxHeight: 120,
            style: {
              colors: 'var(--kate-type-alt-dark)',
              fontSize: '12px',
            },
          },
          tooltip: {
            enabled: !this.customTooltip,
          },
        },
        yaxis: {
          min: 0,
          stepSize: this.stepSize,
          max: this.yaxisMax,
          showForNullSeries: false,
          title: {
            text: this.yaxisTitle,
            style: {
              color: 'var(--kate-type-alt-dark)',
              fontSize: '12px',
            },
          },
          labels: {
            show: true,
            style: {
              colors: 'var(--kate-type-alt-dark)',
            },
            formatter: this.yaxisFormatter || (value => {
              const numericValue = Number(value);
              return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
            }),
          },
        },
        tooltip: {
          enabled: true,
          fillSeriesColor: false,
          shared: true,
          theme: 'dark',
          style: {
            fontSize: '12px',
          },
          markers: {
            show: false,
          },
          custom: this.customTooltip || undefined,
        },
        annotations: {
          xaxis: this.xaxisAnnotations,
        },
      };
    },
  },

  methods: {
    onChartMounted(chartContext) {
      this.bindZoomClamp(chartContext);
      this.applyXaxisAnnotations();
    },

    onChartUpdated(chartContext) {
      this.bindZoomClamp(chartContext);
      this.applyXaxisAnnotations();
    },

    applyXaxisAnnotations() {
      const chart = this.$refs.apexChart?.chart;
      if (!chart || !this.xaxisAnnotations.length) return;

      chart.clearAnnotations();
      this.xaxisAnnotations.forEach(annotation => {
        chart.addXaxisAnnotation(annotation, false);
      });
    },

    // vue3-apexcharts replaces chart.events handlers with emit wrappers on init,
    // so attach beforeZoom directly on the ApexCharts instance instead.
    bindZoomClamp(chartContext) {
      const chartEvents = chartContext?.w?.config?.chart?.events;
      if (!chartEvents) return;
      chartEvents.beforeZoom = (ctx, range) => this.clampZoomToDataRange(ctx, range);
    },

    clampZoomToDataRange(chartContext, { xaxis }) {
      const { initialMinX, initialMaxX } = chartContext.w.globals;
      return {
        xaxis: {
          min: Math.max(xaxis.min, initialMinX),
          max: Math.min(xaxis.max, initialMaxX),
        },
      };
    },
  },
};
</script>
